/**
 * Which light and which finish each part of the building gets.
 *
 * Plain tables, keyed on the words the level already uses: a room's id, its
 * zone and its floor. Nothing here is a gameplay value — the zone decides who
 * may stand in a room, and this only reads it to decide what colour the
 * ceiling light is there.
 */

const FT = 0.3048;

/**
 * The light each room is lit by.
 *
 *   colour     the light's colour
 *   fill       how strongly the room's own light is baked into its surfaces
 *   spacing    feet between fixtures, which is where the pools on the floor fall
 *   fixture    what hangs from the ceiling
 *   ambient    the fill on anybody standing in it, when the camera is there
 */
export const LIGHTS = {
  fluorescent: { colour: 0xe6efff, fill: 1.05, spacing: 10, fixture: 'troffer', ambient: { colour: 0xcfdaf0, level: 0.55 } },
  kitchen: { colour: 0xf2f7ff, fill: 1.25, spacing: 8, fixture: 'troffer', ambient: { colour: 0xdce6f5, level: 0.65 } },
  breakroom: { colour: 0xfff1dc, fill: 1.0, spacing: 10, fixture: 'troffer', ambient: { colour: 0xf0e2cc, level: 0.55 } },
  office: { colour: 0xffe4c4, fill: 0.9, spacing: 8, fixture: 'can', ambient: { colour: 0xf2dcc0, level: 0.5 } },
  chandelier: { colour: 0xffe4c0, fill: 1.1, spacing: 30, fixture: 'chandelier', ambient: { colour: 0xf0c890, level: 0.5 } },
  lobby: { colour: 0xfff0dc, fill: 1.35, spacing: 22, fixture: 'pendant', ambient: { colour: 0xfff0e0, level: 0.8 } },
  outdoors: null,
};

/** The light a room is lit by, from its id and zone. */
export function lightOf(room) {
  if (!room || !room.ceiling) return 'outdoors';
  if (room.id === 'kitchen') return 'kitchen';
  if (room.id === 'break') return 'breakroom';
  if (room.id === 'office') return 'office';
  if (room.zone === 'event') return 'chandelier';
  if (room.zone === 'foh') return 'lobby';
  return 'fluorescent';
}

/** A room's floor finish: the floor word, except where the room makes it special. */
export function floorOf(room) {
  if (room.floor === 'carpet' && room.zone === 'event') return 'ballroomCarpet';
  return room.floor ?? 'concrete';
}

/** What the face of a wall looks like from the room it faces. Outside is stucco. */
export function wallOf(room) {
  if (!room || !room.ceiling) return { surface: 'stucco' };
  if (room.zone === 'event') return { surface: 'damask' };
  if (room.zone === 'foh') return { surface: 'travertine' };
  if (room.id === 'office') return { surface: 'plaster' };
  if (room.id === 'kitchen') return { surface: 'tile', tint: 0xf4f6f8 };
  if (room.id === 'break') return { surface: 'block', tint: 0xf3ead8 };
  return { surface: 'block' };
}

/** What the ceiling looks like from below. */
export function ceilingOf(room) {
  if (room.zone === 'event' || room.zone === 'foh') return 'ceilingPlaster';
  return 'acoustic';
}

/**
 * The finish on a prop, from what its id says it is. A crate is wood and a rack
 * is steel whoever put it there; anything unrecognised keeps its flat colour.
 */
export function propSurface(id) {
  if (/dance-floor/.test(id)) return { surface: 'parquet' };
  if (/pallet|crate|bench|table|stage-deck|risers/.test(id)) return { surface: 'wood' };
  if (/desk|stage-leg/.test(id)) return { surface: 'darkWood' };
  if (/rack|shelves|pass|range|walkin|scaffold|cabinet|lockers|rail|fence|shelter|net|vending|gangbox|bin|skip|cage/.test(id)) return { surface: 'steel', keepColour: true };
  if (/kerb|platform|step/.test(id)) return { surface: 'concrete' };
  if (/cart-path/.test(id)) return { surface: 'gravel', tint: 0xd8cfc0 };
  if (/green-edge/.test(id)) return { surface: 'gravel', tint: 0x8fa070 };
  if (/lobby-col/.test(id)) return { surface: 'marble' };
  if (/^col-/.test(id)) return { surface: 'damask' };
  if (/^airwall-/.test(id)) return { surface: 'damask', tint: 0xe8dcc8 };
  return null;
}

/** Room rectangles in metres, for asking which room a point is in. */
export function roomIndex(rooms) {
  const boxes = rooms.map((room) => {
    const [x0, z0, x1, z1] = room.rect;
    return {
      room,
      minX: Math.min(x0, x1) * FT,
      maxX: Math.max(x0, x1) * FT,
      minZ: Math.min(z0, z1) * FT,
      maxZ: Math.max(z0, z1) * FT,
    };
  });
  return (x, z) => {
    for (const box of boxes) {
      if (x >= box.minX && x <= box.maxX && z >= box.minZ && z <= box.maxZ) return box.room;
    }
    return null;
  };
}
